import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Plus, Trash2, Pencil, Save, Copy, CheckCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Modal } from '@/components/ui/Modal';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  formatMoney,
  useAddQuoteLine,
  useQuote,
  useRemoveQuoteLine,
  useSendQuote,
  useUpdateQuoteDraft,
  useUpdateQuoteLine,
  useWithdrawQuote,
  useConvertQuoteToInvoice,
  type QuoteLineRow,
} from '@/hooks/useQuotes';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { QuoteStatusBadge } from './Quotes';
import { usePatient } from '@/hooks/usePatients';

interface LineDraft {
  description: string;
  quantity: number;
  unit: string;
  unit_price_cents: number;
  vat_rate_percent: number;
}

const EMPTY_LINE: LineDraft = {
  description: '',
  quantity: 1,
  unit: 'stk',
  unit_price_cents: 0,
  vat_rate_percent: 25,
};

export function QuoteDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();

  const { data: quote, isLoading, error } = useQuote(id);
  const updateDraft = useUpdateQuoteDraft();
  const addLine = useAddQuoteLine();
  const updateLine = useUpdateQuoteLine();
  const removeLine = useRemoveQuoteLine();
  const sendQuote = useSendQuote();
  const withdraw = useWithdrawQuote();
  const convert = useConvertQuoteToInvoice();

  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [showAddLine, setShowAddLine] = useState(false);
  const [newLine, setNewLine] = useState<LineDraft>(EMPTY_LINE);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editLine, setEditLine] = useState<LineDraft>(EMPTY_LINE);

  // Modals
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [sentResult, setSentResult] = useState<{ token: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);

  const canAdmin = useMemo(
    () => Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId))),
    [activeClinicId, isPlatformAdmin, isClinicAdmin],
  );

  useEffect(() => {
    if (!quote) return;
    setValidUntil(quote.valid_until ? quote.valid_until.slice(0, 10) : '');
    setNotes(quote.notes ?? '');
  }, [quote]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page-light dark:bg-page-dark">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (error || !quote) {
    return (
      <div className="min-h-screen bg-page-light p-6 dark:bg-page-dark">
        <ErrorAlert message={t('quotes.load_error')} />
      </div>
    );
  }

  const isDraft = quote.status === 'draft';
  const isSent = quote.status === 'sent';
  const isAccepted = quote.status === 'accepted';
  const patientSnap = (quote.patient_snapshot ?? {}) as {
    first_name?: string;
    last_name?: string;
    email?: string | null;
    phone?: string | null;
    address?: Record<string, string | null> | null;
    pat_number?: string | null;
  };
  const clinicSnap = (quote.clinic_snapshot ?? {}) as { name?: string };

  const publicUrl = `${window.location.origin}/accept-quote/${sentResult?.token ?? ''}`;

  // ── Save draft ──────────────────────────────────────────────────────────────
  async function handleSaveDraft(e: FormEvent) {
    e.preventDefault();
    if (!quote) return;
    try {
      await updateDraft.mutateAsync({
        id: quote.id,
        valid_until: validUntil || undefined,
        notes: notes.trim() || undefined,
      });
      toast.success(t('quotes.toast_saved'));
    } catch (err) {
      console.error('update draft failed', err);
      toast.error(t('quotes.save_error'));
    }
  }

  // ── Line CRUD ───────────────────────────────────────────────────────────────
  async function handleAddLine(e: FormEvent) {
    e.preventDefault();
    if (!quote) return;
    try {
      await addLine.mutateAsync({
        quote_id: quote.id,
        description: newLine.description.trim(),
        quantity: newLine.quantity,
        unit: newLine.unit,
        unit_price_cents: newLine.unit_price_cents,
        vat_rate_percent: newLine.vat_rate_percent,
      });
      toast.success(t('quotes.toast_line_added'));
      setNewLine(EMPTY_LINE);
      setShowAddLine(false);
    } catch (err) {
      console.error('add line failed', err);
      toast.error(t('quotes.save_error'));
    }
  }

  function startEditLine(line: QuoteLineRow) {
    setEditingLineId(line.id);
    setEditLine({
      description: line.description,
      quantity: Number(line.quantity),
      unit: line.unit,
      unit_price_cents: line.unit_price_cents,
      vat_rate_percent: Number(line.vat_rate_percent),
    });
  }

  async function handleUpdateLine(lineId: string, e: FormEvent) {
    e.preventDefault();
    if (!quote) return;
    try {
      await updateLine.mutateAsync({
        line_id: lineId,
        quote_id: quote.id,
        description: editLine.description.trim(),
        quantity: editLine.quantity,
        unit: editLine.unit,
        unit_price_cents: editLine.unit_price_cents,
        vat_rate_percent: editLine.vat_rate_percent,
      });
      toast.success(t('quotes.toast_line_updated'));
      setEditingLineId(null);
    } catch (err) {
      console.error('update line failed', err);
      toast.error(t('quotes.save_error'));
    }
  }

  async function handleRemoveLine(line: QuoteLineRow) {
    if (!quote) return;
    if (!confirm(t('quotes.lines_remove') + '?')) return;
    try {
      await removeLine.mutateAsync({ line_id: line.id, quote_id: quote.id });
      toast.success(t('quotes.toast_line_removed'));
    } catch (err) {
      console.error('remove line failed', err);
      toast.error(t('quotes.save_error'));
    }
  }

  // ── Send ────────────────────────────────────────────────────────────────────
  async function handleSend() {
    if (!quote) return;
    try {
      const result = await sendQuote.mutateAsync(quote.id);
      setSentResult({ token: result.accept_token, url: result.accept_url });
      setShowSendModal(true);
      toast.success(t('quotes.toast_sent'));
    } catch (err) {
      console.error('send quote failed', err);
      toast.error(t('quotes.send_error'));
    }
  }

  // ── Withdraw ────────────────────────────────────────────────────────────────
  async function handleWithdraw(e: FormEvent) {
    e.preventDefault();
    if (!quote) return;
    try {
      await withdraw.mutateAsync({
        quote_id: quote.id,
        reason: withdrawReason.trim() || undefined,
      });
      toast.success(t('quotes.toast_withdrawn'));
      setShowWithdrawModal(false);
      setWithdrawReason('');
    } catch (err) {
      console.error('withdraw failed', err);
      toast.error(t('quotes.withdraw_error'));
    }
  }

  // ── Convert to invoice ──────────────────────────────────────────────────────
  async function handleConvert() {
    if (!quote) return;
    try {
      const invoice = await convert.mutateAsync(quote.id);
      toast.success(t('quotes.toast_converted'));
      setShowConvertModal(false);
      navigate(`/invoices/${invoice.id}`);
    } catch (err) {
      console.error('convert failed', err);
      toast.error(t('quotes.convert_error'));
    }
  }

  async function copyPatientLink() {
    if (!sentResult) return;
    const url = `${window.location.origin}/accept-quote/${sentResult.token}`;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="mx-auto max-w-4xl space-y-6 px-6">
          <button
            type="button"
            onClick={() => navigate('/economy/quotes')}
            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('quotes.back_to_list')}
          </button>

          {/* Header card */}
          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {quote.quote_number ?? t('quotes.draft_label')}
                  </h1>
                  <QuoteStatusBadge status={quote.status} />
                </div>

                {patientSnap.first_name ? (
                  <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {patientSnap.first_name} {patientSnap.last_name}
                    {patientSnap.pat_number && (
                      <span className="ml-2 font-mono text-xs">{patientSnap.pat_number}</span>
                    )}
                  </div>
                ) : (
                  <PatientFallback patientId={quote.patient_id} />
                )}

                {quote.sent_at && (
                  <div className="mt-1 text-xs text-gray-500">
                    {t('quotes.sent_at_label')}:{' '}
                    {new Date(quote.sent_at).toLocaleDateString('da-DK')}
                  </div>
                )}
                {quote.accepted_at && (
                  <div className="mt-1 text-xs text-emerald-700 dark:text-emerald-400">
                    {t('quotes.accepted_at_label')}:{' '}
                    {new Date(quote.accepted_at).toLocaleDateString('da-DK')}
                  </div>
                )}
                {quote.rejected_at && (
                  <div className="mt-1 text-xs text-red-700 dark:text-red-400">
                    {t('quotes.rejected_at_label')}:{' '}
                    {new Date(quote.rejected_at).toLocaleDateString('da-DK')}
                  </div>
                )}
                {quote.withdrawn_at && (
                  <div className="mt-1 text-xs text-gray-500">
                    {t('quotes.withdrawn_at_label')}:{' '}
                    {new Date(quote.withdrawn_at).toLocaleDateString('da-DK')}
                  </div>
                )}
                {quote.converted_invoice_id && (
                  <button
                    type="button"
                    onClick={() => navigate(`/invoices/${quote.converted_invoice_id}`)}
                    className="mt-1 text-xs text-indigo-600 hover:underline dark:text-indigo-400"
                  >
                    {t('quotes.see_invoice')}
                  </button>
                )}
              </div>

              <div className="flex flex-col items-end gap-2">
                {isDraft && canAdmin && (
                  <Button
                    onClick={handleSend}
                    disabled={sendQuote.isPending || quote.lines.length === 0}
                  >
                    {sendQuote.isPending ? t('common.saving') : t('quotes.send')}
                  </Button>
                )}
                {isAccepted && canAdmin && !quote.converted_invoice_id && (
                  <Button onClick={() => setShowConvertModal(true)}>
                    {t('quotes.convert_to_invoice')}
                  </Button>
                )}
                {(isDraft || isSent) && canAdmin && (
                  <Button variant="secondary" onClick={() => setShowWithdrawModal(true)}>
                    {t('quotes.withdraw')}
                  </Button>
                )}
                {clinicSnap.name && (
                  <div className="text-right text-xs text-gray-500">{clinicSnap.name}</div>
                )}
              </div>
            </div>

            {/* Patient link — shown when quote is sent */}
            {isSent && sentResult && (
              <div className="mt-4 rounded-md border border-indigo-200 bg-indigo-50 p-3 dark:border-indigo-700 dark:bg-indigo-900/20">
                <p className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
                  {t('quotes.patient_link_label')}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 truncate rounded bg-white px-2 py-1 font-mono text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    {publicUrl}
                  </code>
                  <Button size="sm" variant="secondary" onClick={copyPatientLink}>
                    {copied ? (
                      <CheckCheck className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    <span className="ml-1">
                      {copied ? t('quotes.copied') : t('quotes.copy_patient_link')}
                    </span>
                  </Button>
                </div>
              </div>
            )}
          </section>

          {/* Draft fields */}
          {isDraft && (
            <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
              <form onSubmit={handleSaveDraft} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Input
                    label={t('quotes.field_valid_until')}
                    type="date"
                    value={validUntil}
                    onChange={(e) => setValidUntil(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('quotes.field_notes')}
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={updateDraft.isPending}>
                    {updateDraft.isPending ? t('common.saving') : t('quotes.save_draft')}
                  </Button>
                </div>
              </form>
            </section>
          )}

          {!isDraft && (quote.valid_until || quote.notes) && (
            <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
              {quote.valid_until && (
                <div className="text-sm">
                  <span className="text-gray-500">{t('quotes.field_valid_until')}: </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {new Date(quote.valid_until).toLocaleDateString('da-DK')}
                  </span>
                </div>
              )}
              {quote.notes && (
                <div className="mt-2 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
                  {quote.notes}
                </div>
              )}
            </section>
          )}

          {/* Lines */}
          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                {t('quotes.lines_title')}
              </h2>
              {isDraft && !showAddLine && (
                <Button size="sm" onClick={() => setShowAddLine(true)}>
                  <Plus className="mr-1 h-4 w-4" />
                  {t('quotes.lines_add')}
                </Button>
              )}
            </div>

            {quote.lines.length === 0 && !showAddLine ? (
              <p className="rounded-md border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                {t('quotes.lines_empty')}
              </p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <Th>{t('quotes.line_field_description')}</Th>
                      <Th>{t('quotes.line_field_quantity')}</Th>
                      <Th>{t('quotes.line_field_unit')}</Th>
                      <Th>{t('quotes.line_field_unit_price')}</Th>
                      <Th>{t('quotes.line_field_vat')}</Th>
                      <Th>{t('quotes.totals_total')}</Th>
                      <th className="relative px-3 py-3">
                        <span className="sr-only">{t('quotes.col_actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
                    {quote.lines.map((line) =>
                      editingLineId === line.id ? (
                        <tr key={line.id}>
                          <td colSpan={7} className="p-3">
                            <form
                              onSubmit={(e) => handleUpdateLine(line.id, e)}
                              className="grid grid-cols-1 gap-3 md:grid-cols-7"
                            >
                              <Input
                                label={t('quotes.line_field_description')}
                                value={editLine.description}
                                onChange={(e) =>
                                  setEditLine({ ...editLine, description: e.target.value })
                                }
                                className="md:col-span-2"
                                required
                              />
                              <Input
                                label={t('quotes.line_field_quantity')}
                                type="number"
                                step="0.001"
                                value={editLine.quantity.toString()}
                                onChange={(e) =>
                                  setEditLine({ ...editLine, quantity: Number(e.target.value) })
                                }
                              />
                              <Input
                                label={t('quotes.line_field_unit')}
                                value={editLine.unit}
                                onChange={(e) => setEditLine({ ...editLine, unit: e.target.value })}
                              />
                              <Input
                                label={t('quotes.line_field_unit_price')}
                                type="number"
                                step="0.01"
                                value={(editLine.unit_price_cents / 100).toString()}
                                onChange={(e) =>
                                  setEditLine({
                                    ...editLine,
                                    unit_price_cents: Math.round(Number(e.target.value) * 100),
                                  })
                                }
                              />
                              <Input
                                label={t('quotes.line_field_vat')}
                                type="number"
                                value={editLine.vat_rate_percent.toString()}
                                onChange={(e) =>
                                  setEditLine({
                                    ...editLine,
                                    vat_rate_percent: Number(e.target.value),
                                  })
                                }
                              />
                              <div className="flex items-end gap-2 md:col-span-7">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => setEditingLineId(null)}
                                >
                                  {t('common.cancel')}
                                </Button>
                                <Button type="submit" size="sm" disabled={updateLine.isPending}>
                                  <Save className="mr-1 h-4 w-4" />
                                  {t('quotes.lines_save')}
                                </Button>
                              </div>
                            </form>
                          </td>
                        </tr>
                      ) : (
                        <tr key={line.id}>
                          <td className="px-3 py-3 text-sm text-gray-900 dark:text-white">
                            {line.description}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {line.quantity}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {line.unit}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {formatMoney(line.unit_price_cents, quote.currency)}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {line.vat_rate_percent}%
                          </td>
                          <td className="px-3 py-3 text-sm font-medium text-gray-900 dark:text-white">
                            {formatMoney(line.line_total_cents, quote.currency)}
                          </td>
                          <td className="px-3 py-3 text-right text-sm">
                            {isDraft && (
                              <div className="inline-flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => startEditLine(line)}
                                  className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400"
                                  aria-label={t('common.edit')}
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveLine(line)}
                                  className="text-red-600 hover:text-red-800 dark:text-red-400"
                                  aria-label={t('quotes.lines_remove')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {showAddLine && isDraft && (
              <form
                onSubmit={handleAddLine}
                className="mt-4 grid grid-cols-1 gap-3 rounded-md border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800 md:grid-cols-7"
              >
                <Input
                  label={t('quotes.line_field_description')}
                  value={newLine.description}
                  onChange={(e) => setNewLine({ ...newLine, description: e.target.value })}
                  required
                  className="md:col-span-2"
                />
                <Input
                  label={t('quotes.line_field_quantity')}
                  type="number"
                  step="0.001"
                  value={newLine.quantity.toString()}
                  onChange={(e) => setNewLine({ ...newLine, quantity: Number(e.target.value) })}
                />
                <Input
                  label={t('quotes.line_field_unit')}
                  value={newLine.unit}
                  onChange={(e) => setNewLine({ ...newLine, unit: e.target.value })}
                />
                <Input
                  label={t('quotes.line_field_unit_price')}
                  type="number"
                  step="0.01"
                  value={(newLine.unit_price_cents / 100).toString()}
                  onChange={(e) =>
                    setNewLine({
                      ...newLine,
                      unit_price_cents: Math.round(Number(e.target.value) * 100),
                    })
                  }
                />
                <Input
                  label={t('quotes.line_field_vat')}
                  type="number"
                  value={newLine.vat_rate_percent.toString()}
                  onChange={(e) =>
                    setNewLine({ ...newLine, vat_rate_percent: Number(e.target.value) })
                  }
                />
                <div className="flex items-end gap-2 md:col-span-7">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowAddLine(false);
                      setNewLine(EMPTY_LINE);
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" size="sm" disabled={addLine.isPending}>
                    {addLine.isPending ? t('common.saving') : t('quotes.lines_add')}
                  </Button>
                </div>
              </form>
            )}

            {/* Totals */}
            <div className="ml-auto mt-6 max-w-xs space-y-1 text-sm">
              <Row label={t('quotes.totals_subtotal')}>
                {formatMoney(quote.subtotal_cents, quote.currency)}
              </Row>
              <Row label={t('quotes.totals_vat')}>
                {formatMoney(quote.vat_cents, quote.currency)}
              </Row>
              <Row label={t('quotes.totals_total')} bold>
                {formatMoney(quote.total_cents, quote.currency)}
              </Row>
            </div>
          </section>

          {/* Withdraw modal */}
          {showWithdrawModal && (
            <Modal onClose={() => setShowWithdrawModal(false)} title={t('quotes.withdraw')}>
              <form onSubmit={handleWithdraw} className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('quotes.confirm_withdraw')}
                </p>
                <Input
                  label={t('quotes.field_withdraw_reason')}
                  value={withdrawReason}
                  onChange={(e) => setWithdrawReason(e.target.value)}
                />
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowWithdrawModal(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={withdraw.isPending}>
                    {withdraw.isPending ? t('common.saving') : t('quotes.withdraw')}
                  </Button>
                </div>
              </form>
            </Modal>
          )}

          {/* Send result modal — shows patient link */}
          {showSendModal && sentResult && (
            <Modal onClose={() => setShowSendModal(false)} title={t('quotes.send')}>
              <div className="space-y-4">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('quotes.send_success_message')}
                </p>
                <div>
                  <p className="mb-1 text-xs font-medium text-gray-600 dark:text-gray-400">
                    {t('quotes.patient_link_label')}
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 truncate rounded border border-gray-200 bg-gray-50 px-2 py-1.5 font-mono text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                      {publicUrl}
                    </code>
                    <Button size="sm" variant="secondary" onClick={copyPatientLink}>
                      {copied ? (
                        <CheckCheck className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  {t('quotes.send_token_warning')}
                </p>
                <div className="flex justify-end">
                  <Button onClick={() => setShowSendModal(false)}>{t('common.close')}</Button>
                </div>
              </div>
            </Modal>
          )}

          {/* Convert modal */}
          {showConvertModal && (
            <Modal
              onClose={() => setShowConvertModal(false)}
              title={t('quotes.convert_to_invoice')}
            >
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('quotes.confirm_convert')}
                </p>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowConvertModal(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button onClick={handleConvert} disabled={convert.isPending}>
                    {convert.isPending ? t('common.saving') : t('quotes.convert_to_invoice')}
                  </Button>
                </div>
              </div>
            </Modal>
          )}
        </div>
      </main>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300"
    >
      {children}
    </th>
  );
}

function Row({
  label,
  bold,
  children,
}: {
  label: string;
  bold?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`flex items-center justify-between ${bold ? 'border-t border-gray-200 pt-1 font-semibold dark:border-gray-700' : ''}`}
    >
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-gray-900 dark:text-white">{children}</span>
    </div>
  );
}

function PatientFallback({ patientId }: { patientId: string }) {
  const { data: patient } = usePatient(patientId);
  if (!patient) return null;
  return (
    <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
      {patient.first_name} {patient.last_name}
      {patient.pat_number && <span className="ml-2 font-mono text-xs">{patient.pat_number}</span>}
    </div>
  );
}
